import React, { useState, useEffect } from "react";
import {
  Send,
  CheckCircle2,
  MessageCircle,
  CalendarCheck,
  Mail,
  Phone,
  Shield,
  Clock,
} from "lucide-react";
import { createPaymentOrder, submitContactForm, verifyPayment } from "../services/api";
import { BUSINESS } from "../lib/businessInfo";
import { openRazorpayCheckout } from "../lib/razorpayCheckout";
import DateField, { todayIso } from "./DateField";

const PROGRAMS = [
  "Yoga Retreat in Rishikesh",
  "Yoga Retreat in Goa",
  "Meditation Retreat in India",
  "200-Hour Yoga Teacher Training",
  "300-Hour Yoga Teacher Training",
  "Ayurveda & Wellness Retreat",
  "Other / General Inquiry",
];

const FORM_TYPES = {
  enquiry: {
    label: "Enquire",
    headline: "Have a question?",
    title: "Send an Enquiry",
    description:
      "Not sure about dates, pricing, or what's included? Ask us anything — we'll reply within 24 hours.",
    submitLabel: "Send Enquiry",
    icon: MessageCircle,
    accent: "sky",
    headerGradient: "from-sky-600 to-blue-700",
    tabActive: "bg-sky-600 text-white shadow-md",
    tabInactive: "bg-white text-sky-700 border border-sky-200 hover:bg-sky-50",
    ring: "focus:border-sky-400 focus:ring-sky-100",
    button: "bg-sky-600 hover:bg-sky-700 shadow-sky-200",
    badge: "bg-sky-100 text-sky-800 border-sky-200",
    panel: "bg-sky-50 border-sky-100",
  },
  book: {
    label: "Book Now",
    headline: "Ready to book?",
    title: "Request Your Spot",
    description:
      "Share your travel dates and room preference. Pay securely online to confirm, or request a callback if payment isn't available yet.",
    submitLabel: "Submit Booking Request",
    paySubmitLabel: "Pay & Confirm Booking",
    icon: CalendarCheck,
    accent: "emerald",
    headerGradient: "from-emerald-600 to-teal-700",
    tabActive: "bg-emerald-600 text-white shadow-md",
    tabInactive: "bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-50",
    ring: "focus:border-emerald-400 focus:ring-emerald-100",
    button: "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
    panel: "bg-emerald-50 border-emerald-100",
  },
};

function buildInitialForm(retreat) {
  return {
    name: "",
    email: "",
    phone: "",
    program: retreat?.retreat_name || "",
    enquiryDate: todayIso(),
    preferredStartDate: "",
    preferredEndDate: "",
    guests: "1",
    roomPreference: "",
    message: "",
  };
}

function inputClass(ring) {
  return `w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:ring-2 outline-none text-sm text-gray-900 placeholder-gray-400 transition-colors ${ring}`;
}

function canPayOnline(target) {
  const price = Number(target?.price_inr);
  return Boolean(
    target &&
      (target.retreat_key || target.offering_key || target.slug) &&
      Number.isFinite(price) &&
      price > 0
  );
}

export default function RetreatInquiryForm({
  defaultFormType = "enquiry",
  showTypeToggle = true,
  retreat = null,
  offerings = [],
  variant = "page",
  className = "",
}) {
  const payableOfferings = Array.isArray(offerings)
    ? offerings.filter((item) => Number(item?.price_inr) > 0 && (item.offering_key || item.slug))
    : [];
  const hasOfferingPicker = payableOfferings.length > 0;

  const [formType, setFormType] = useState(defaultFormType);
  const [form, setForm] = useState(() => buildInitialForm(retreat));
  const [selectedOfferingKey, setSelectedOfferingKey] = useState(
    () => payableOfferings[0]?.offering_key || payableOfferings[0]?.slug || ""
  );
  const [submitted, setSubmitted] = useState(false);
  const [paid, setPaid] = useState(false);
  const [paidAmountInr, setPaidAmountInr] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [website, setWebsite] = useState("");

  const payableOfferingKeys = payableOfferings
    .map((item) => item.offering_key || item.slug)
    .join("|");

  useEffect(() => {
    if (!hasOfferingPicker) return;
    const stillValid = payableOfferings.some(
      (item) => (item.offering_key || item.slug) === selectedOfferingKey
    );
    if (!stillValid) {
      setSelectedOfferingKey(payableOfferings[0].offering_key || payableOfferings[0].slug || "");
    }
  }, [hasOfferingPicker, selectedOfferingKey, payableOfferingKeys]);

  const selectedOffering = hasOfferingPicker
    ? payableOfferings.find((item) => (item.offering_key || item.slug) === selectedOfferingKey) ||
      payableOfferings[0]
    : null;

  const paymentTarget = selectedOffering
    ? {
        retreat_key: selectedOffering.offering_key || "",
        offering_key: selectedOffering.offering_key || "",
        slug: selectedOffering.slug || "",
        retreat_name: selectedOffering.title,
        price_inr: selectedOffering.price_inr,
      }
    : retreat;

  const config = FORM_TYPES[formType];
  const isBook = formType === "book";
  const isSection = variant === "section";
  const paymentEnabled = isBook && canPayOnline(paymentTarget);
  const guestsCount = Math.max(1, parseInt(String(form.guests).replace(/[^\d]/g, ""), 10) || 1);
  const displayTotalInr = paymentEnabled
    ? Math.round(Number(paymentTarget.price_inr) * guestsCount)
    : null;
  const selectedPanelTitle = selectedOffering
    ? selectedOffering.title
    : retreat?.retreat_name;

  useEffect(() => {
    if (!isSection) return;
    const syncFromHash = () => {
      const hash = window.location.hash;
      if (hash === "#enquire") setFormType("enquiry");
      else if (hash === "#book-enquire") setFormType("book");
    };
    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, [isSection]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleTypeChange = (type) => {
    setFormType(type);
    setError("");
  };

  const buildContactPayload = (preferredDates) => {
    const listingName =
      paymentTarget?.retreat_name ||
      selectedOffering?.title ||
      form.program ||
      retreat?.retreat_name ||
      "";
    return {
      form_type: formType,
      name: form.name,
      email: form.email,
      phone: form.phone,
      program: listingName || form.program,
      message: form.message,
      enquiry_date: form.enquiryDate,
      preferred_dates: preferredDates,
      preferred_start_date: form.preferredStartDate,
      preferred_end_date: form.preferredEndDate,
      guests: form.guests,
      room_preference: form.roomPreference,
      retreat_key: paymentTarget?.retreat_key || paymentTarget?.offering_key || retreat?.retreat_key || "",
      retreat_name: listingName,
      retreat_slug: paymentTarget?.slug || retreat?.slug || "",
      retreat_location: retreat?.location || "",
      source_url: retreat?.source_url || "",
      website,
    };
  };

  const handlePayAndBook = async (preferredDates) => {
    if (hasOfferingPicker && !selectedOffering) {
      throw new Error("Please select a class to book.");
    }

    const order = await createPaymentOrder(buildContactPayload(preferredDates));
    if (!order?.orderId) {
      throw new Error("Unable to start payment.");
    }

    await new Promise((resolve, reject) => {
      openRazorpayCheckout({
        keyId: order.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
        orderId: order.orderId,
        amount: order.amount,
        currency: order.currency || "INR",
        description:
          order.retreatName ||
          selectedOffering?.title ||
          retreat?.retreat_name ||
          "Class booking",
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone,
        },
        onSuccess: async (response) => {
          try {
            const verified = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setPaid(true);
            setPaidAmountInr(verified?.amountInr ?? order.amountInr ?? null);
            setSubmitted(true);
            resolve();
          } catch (err) {
            reject(err);
          }
        },
        onDismiss: () => {
          reject(new Error("Payment was cancelled. You can try again when ready."));
        },
      }).catch(reject);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");

    try {
      const preferredDates =
        isBook && form.preferredStartDate
          ? form.preferredEndDate && form.preferredEndDate !== form.preferredStartDate
            ? `${form.preferredStartDate} to ${form.preferredEndDate}`
            : form.preferredStartDate
          : "";

      if (paymentEnabled) {
        await handlePayAndBook(preferredDates);
      } else {
        await submitContactForm(buildContactPayload(preferredDates));
        setPaid(false);
        setPaidAmountInr(null);
        setSubmitted(true);
      }
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.message ||
        `Something went wrong. Please email us at ${BUSINESS.email} or WhatsApp ${BUSINESS.phoneDisplay}.`;
      setError(message);
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setPaid(false);
    setPaidAmountInr(null);
    setError("");
    setWebsite("");
    setForm(buildInitialForm(retreat));
    if (hasOfferingPicker) {
      setSelectedOfferingKey(payableOfferings[0]?.offering_key || payableOfferings[0]?.slug || "");
    }
  };

  if (submitted) {
    return (
      <div
        className={`rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-lg ${className}`}
      >
        <div className={`bg-gradient-to-r ${config.headerGradient} px-6 py-8 text-center text-white`}>
          <CheckCircle2 className="mx-auto mb-3" size={48} strokeWidth={1.5} />
          <h3 className="text-2xl font-bold">
            {paid
              ? "Payment Successful!"
              : isBook
                ? "Booking Request Received!"
                : "Enquiry Sent Successfully!"}
          </h3>
        </div>
        <div className="px-6 py-10 text-center">
          <p className="text-gray-600 max-w-md mx-auto leading-relaxed">
            {paid
              ? `Thank you! Your payment${
                  paidAmountInr != null ? ` of ₹${Number(paidAmountInr).toLocaleString("en-IN")}` : ""
                } was received. Our booking team will confirm your spot shortly.`
              : isBook
                ? "Thank you! Our booking team will contact you shortly to confirm availability and next steps."
                : "Thank you for reaching out. We typically respond within 24 hours on business days."}
          </p>
          <button
            type="button"
            onClick={resetForm}
            className={`mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white ${config.button}`}
          >
            {paid ? "Book another retreat" : isBook ? "Submit another request" : "Send another enquiry"}
          </button>
        </div>
      </div>
    );
  }

  const submitLabel = paymentEnabled
    ? displayTotalInr != null
      ? `${config.paySubmitLabel} · ₹${displayTotalInr.toLocaleString("en-IN")}`
      : config.paySubmitLabel
    : config.submitLabel;

  return (
    <div
      className={`rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-lg ${className}`}
    >
      {/* Colored header */}
      <div className={`bg-gradient-to-r ${config.headerGradient} px-6 py-6 sm:px-8 sm:py-7`}>
        {showTypeToggle && (
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            {Object.entries(FORM_TYPES).map(([type, typeConfig]) => {
              const Icon = typeConfig.icon;
              const active = formType === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleTypeChange(type)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-5 rounded-xl text-sm sm:text-base font-bold transition-all ${
                    active ? typeConfig.tabActive : typeConfig.tabInactive
                  }`}
                >
                  <Icon size={18} />
                  {typeConfig.label}
                </button>
              );
            })}
          </div>
        )}

        <p className="text-white/80 text-sm font-medium uppercase tracking-wider mb-1">
          {config.headline}
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold text-white">{config.title}</h2>
        <p className="text-white/90 text-sm sm:text-base mt-2 max-w-2xl leading-relaxed">
          {config.description}
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-0">
        {/* Left info panel — standard on retreat booking sites */}
        <aside className={`lg:col-span-2 border-b lg:border-b-0 lg:border-r border-gray-200 p-6 sm:p-8 ${config.panel}`}>
          {(selectedPanelTitle || retreat?.retreat_name) && (
            <div className={`rounded-xl border px-4 py-4 mb-6 bg-white ${config.badge}`}>
              <p className="text-[11px] font-bold uppercase tracking-widest opacity-70 mb-1">
                {selectedOffering ? "Selected class" : "Selected retreat"}
              </p>
              <p className="font-bold text-gray-900 leading-snug">
                {selectedPanelTitle || retreat?.retreat_name}
              </p>
              {selectedOffering?.schedule && (
                <p className="text-sm text-gray-600 mt-1">{selectedOffering.schedule}</p>
              )}
              {retreat?.location && (
                <p className="text-sm text-gray-600 mt-1">{retreat.location}</p>
              )}
              {paymentEnabled && displayTotalInr != null && (
                <p className="text-sm font-semibold text-emerald-700 mt-2">
                  Pay now: ₹{displayTotalInr.toLocaleString("en-IN")}
                  {guestsCount > 1 ? ` (${guestsCount} guests)` : ""}
                </p>
              )}
            </div>
          )}

          <h3 className="font-bold text-gray-900 mb-4">Why book with us</h3>
          <ul className="space-y-4">
            <li className="flex gap-3">
              <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center shrink-0">
                <Clock size={16} className="text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Reply within 24 hours</p>
                <p className="text-xs text-gray-600 mt-0.5">Fast responses on business days</p>
              </div>
            </li>
            <li className="flex gap-3">
              <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center shrink-0">
                <Shield size={16} className="text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Free & no obligation</p>
                <p className="text-xs text-gray-600 mt-0.5">Enquire before you commit</p>
              </div>
            </li>
          </ul>

          <div className="mt-8 pt-6 border-t border-gray-200/80">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              Prefer to reach us directly?
            </p>
            <div className="flex flex-col gap-2">
              <a
                href={`mailto:${BUSINESS.email}`}
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white border border-gray-200 hover:border-gray-300 transition-colors"
              >
                <Mail size={16} className="text-gray-500" />
                <span className="text-sm font-medium text-gray-800">{BUSINESS.email}</span>
              </a>
              <a
                href={`https://wa.me/${BUSINESS.phone.replace(/\D/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-colors"
              >
                <Phone size={16} className="text-green-600" />
                <span className="text-sm font-medium text-gray-800">{BUSINESS.phoneDisplay}</span>
              </a>
            </div>
          </div>
        </aside>

        {/* Form panel */}
        <div className="lg:col-span-3 p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
            />

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {isBook && hasOfferingPicker && (
              <fieldset>
                <legend className="block text-sm font-semibold text-gray-800 mb-2">
                  Choose a class <span className="text-red-500">*</span>
                </legend>
                <div className="space-y-2">
                  {payableOfferings.map((offering) => {
                    const optionId = offering.offering_key || offering.slug;
                    const checked = optionId === (selectedOffering?.offering_key || selectedOffering?.slug);
                    return (
                      <label
                        key={optionId}
                        className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                          checked
                            ? "border-emerald-500 bg-emerald-50"
                            : "border-gray-200 bg-white hover:border-emerald-200"
                        }`}
                      >
                        <input
                          type="radio"
                          name="selected_offering"
                          value={optionId}
                          checked={checked}
                          onChange={() => setSelectedOfferingKey(optionId)}
                          className="mt-1"
                          required
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-gray-900">
                            {offering.title}
                          </span>
                          {offering.schedule && (
                            <span className="block text-xs text-gray-600 mt-0.5">
                              {offering.schedule}
                            </span>
                          )}
                          <span className="block text-sm font-bold text-emerald-700 mt-1">
                            ₹{Number(offering.price_inr).toLocaleString("en-IN")}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor={`${formType}-name`} className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id={`${formType}-name`}
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className={inputClass(config.ring)}
                />
              </div>

              <div>
                <label htmlFor={`${formType}-email`} className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id={`${formType}-email`}
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className={inputClass(config.ring)}
                />
              </div>

              <div className={!isBook && !retreat ? "sm:col-span-2" : ""}>
                <label htmlFor={`${formType}-phone`} className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Phone / WhatsApp{" "}
                  {isBook ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-gray-400 font-normal text-xs">(optional)</span>
                  )}
                </label>
                <input
                  id={`${formType}-phone`}
                  name="phone"
                  type="tel"
                  required={isBook}
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX"
                  className={inputClass(config.ring)}
                />
              </div>
            </div>

            {!retreat && (
              <div>
                <label htmlFor={`${formType}-program`} className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Program of Interest
                </label>
                <select
                  id={`${formType}-program`}
                  name="program"
                  value={form.program}
                  onChange={handleChange}
                  className={inputClass(config.ring)}
                >
                  <option value="">Select a program...</option>
                  {PROGRAMS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!isBook && (
              <DateField
                id={`${formType}-enquiryDate`}
                name="enquiryDate"
                label="Date of Enquiry"
                value={form.enquiryDate}
                onChange={handleChange}
                required
                ringClass={config.ring}
              />
            )}

            {isBook && (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <DateField
                    id={`${formType}-preferredStartDate`}
                    name="preferredStartDate"
                    label="Preferred Start Date"
                    value={form.preferredStartDate}
                    onChange={(e) => {
                      handleChange(e);
                      if (form.preferredEndDate && e.target.value > form.preferredEndDate) {
                        setForm((prev) => ({ ...prev, preferredEndDate: e.target.value }));
                      }
                    }}
                    required
                    ringClass={config.ring}
                    min={todayIso()}
                  />
                  <DateField
                    id={`${formType}-preferredEndDate`}
                    name="preferredEndDate"
                    label="Preferred End Date"
                    value={form.preferredEndDate}
                    onChange={handleChange}
                    required
                    min={form.preferredStartDate || todayIso()}
                    ringClass={config.ring}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={`${formType}-guests`} className="block text-sm font-semibold text-gray-800 mb-1.5">
                      Number of Guests
                    </label>
                    <select
                      id={`${formType}-guests`}
                      name="guests"
                      value={form.guests}
                      onChange={handleChange}
                      className={inputClass(config.ring)}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                        <option key={n} value={String(n)}>
                          {n} {n === 1 ? "person" : "people"}
                        </option>
                      ))}
                      <option value="9+">9+ people</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor={`${formType}-roomPreference`}
                      className="block text-sm font-semibold text-gray-800 mb-1.5"
                    >
                      Room Preference
                    </label>
                    <select
                      id={`${formType}-roomPreference`}
                      name="roomPreference"
                      value={form.roomPreference}
                      onChange={handleChange}
                      className={inputClass(config.ring)}
                    >
                      <option value="">No preference</option>
                      <option value="Shared">Shared room</option>
                      <option value="Private">Private room</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            <div>
              <label htmlFor={`${formType}-message`} className="block text-sm font-semibold text-gray-800 mb-1.5">
                {isBook ? (
                  <>Special Requests <span className="text-gray-400 font-normal text-xs">(optional)</span></>
                ) : (
                  <>Your Question <span className="text-red-500">*</span></>
                )}
              </label>
              <textarea
                id={`${formType}-message`}
                name="message"
                required={!isBook}
                rows={4}
                value={form.message}
                onChange={handleChange}
                placeholder={
                  isBook
                    ? "Dietary needs, injuries, airport pickup, or anything else..."
                    : "Ask about dates, pricing, inclusions, travel, or anything else..."
                }
                className={`${inputClass(config.ring)} resize-none`}
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className={`flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl text-white text-base font-bold transition-colors shadow-lg disabled:opacity-60 disabled:cursor-not-allowed ${config.button}`}
            >
              {sending ? (
                <>
                  <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  {paymentEnabled ? "Opening payment..." : "Sending..."}
                </>
              ) : (
                <>
                  <Send size={18} />
                  {submitLabel}
                </>
              )}
            </button>

            <p className="text-center text-xs text-gray-500">
              {paymentEnabled
                ? "Secure payment via Razorpay. By paying, you agree to be contacted about your booking."
                : `By submitting, you agree to be contacted about your ${isBook ? "booking" : "enquiry"}. We never share your details.`}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
